// AuthModalContext.tsx - Global auth modal state
import React, { createContext, useContext, useState, ReactNode } from 'react';

interface AuthModalContextType {
  isOpen: boolean;
  mode: 'signin' | 'signup';
  isRequestingContext: boolean;
  openModal: (isRequesting?: boolean) => void;
  openSignupModal: (isRequesting?: boolean) => void;
  closeModal: () => void;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export const AuthModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [isRequestingContext, setIsRequestingContext] = useState(false);

  const openModal = (isRequesting: boolean = false) => {
    setMode('signin');
    setIsRequestingContext(isRequesting);
    setIsOpen(true);
  };

  const openSignupModal = (isRequesting: boolean = false) => {
    setMode('signup');
    setIsRequestingContext(isRequesting);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setIsRequestingContext(false);
  };

  return (
    <AuthModalContext.Provider value={{ isOpen, mode, isRequestingContext, openModal, openSignupModal, closeModal }}>
      {children}
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = (): AuthModalContextType => {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within AuthModalProvider');
  }
  return context;
};
